import { Injectable, computed, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import {
  BankAccount,
  ContractorDocument,
  ContractorDocumentType
} from '@umss/shared/data-access';
import { ProfileSetupStepType } from '../models/profile-setup-step-type';
import { PaymentService } from './payment.service';
import { ProfileService } from './profile.service';
import { ProfileStateService } from './profile-state.service';

const NON_BG_CHECK_STEP_TYPES = [
  ProfileSetupStepType.BankAccount,
  ProfileSetupStepType.DriversLicense,
  ProfileSetupStepType.ProfessionalLicense,
  ProfileSetupStepType.I9Form,
  ProfileSetupStepType.CprCertification,
  ProfileSetupStepType.ImmunizationRecords,
  ProfileSetupStepType.BlsCertification
] as const;

/**
 * Root-scoped service that computes whether all non-background-check
 * profile steps are complete. Used by components outside the profile
 * setup card (e.g. settings page, background check section).
 */
@Injectable({ providedIn: 'root' })
export class ProfileCompletionService {
  private readonly profileState = inject(ProfileStateService);
  private readonly profileService = inject(ProfileService);
  private readonly paymentService = inject(PaymentService);

  private readonly documentsSignal = signal<ContractorDocument[]>([]);
  private readonly bankAccountsSignal = signal<BankAccount[]>([]);
  private readonly loadedSignal = signal(false);

  readonly loaded = this.loadedSignal.asReadonly();

  readonly allOtherStepsComplete = computed(() => {
    if (!this.loadedSignal()) return false;
    const profile = this.profileState.profile();
    const documents = this.documentsSignal();
    const bankAccounts = this.bankAccountsSignal();

    return NON_BG_CHECK_STEP_TYPES.every((type) =>
      this.isStepComplete(type, profile, documents, bankAccounts)
    );
  });

  load(): void {
    if (this.loadedSignal()) return;

    this.profileState.whenReady().subscribe((profile) => {
      forkJoin({
        documents: this.profileService.getDocuments(profile.id, {
          documentTypes: [
            ContractorDocumentType.DRIVERS_LICENSE,
            ContractorDocumentType.I9_FORM,
            ContractorDocumentType.CPR_CERTIFICATION,
            ContractorDocumentType.IMMUNIZATION_RECORDS,
            ContractorDocumentType.BLS_CERTIFICATION
          ]
        }),
        bankAccounts: this.paymentService.getMyBankAccounts()
      }).subscribe({
        next: ({ documents, bankAccounts }) => {
          this.documentsSignal.set(documents);
          this.bankAccountsSignal.set(bankAccounts);
          this.loadedSignal.set(true);
        }
      });
    });
  }

  private isStepComplete(
    type: ProfileSetupStepType,
    profile: ReturnType<typeof this.profileState.profile>,
    documents: ContractorDocument[],
    bankAccounts: BankAccount[]
  ): boolean {
    switch (type) {
      case ProfileSetupStepType.BankAccount:
        return bankAccounts.length > 0;
      case ProfileSetupStepType.DriversLicense:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.DRIVERS_LICENSE
        );
      case ProfileSetupStepType.ProfessionalLicense:
        return !!profile?.licenseNumber;
      case ProfileSetupStepType.I9Form:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.I9_FORM
        );
      case ProfileSetupStepType.CprCertification:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.CPR_CERTIFICATION
        );
      case ProfileSetupStepType.ImmunizationRecords:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.IMMUNIZATION_RECORDS
        );
      case ProfileSetupStepType.BlsCertification:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.BLS_CERTIFICATION
        );
      default:
        return false;
    }
  }
}
